import { memo, useState, useRef, useEffect } from "react";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import { Link } from "react-router-dom";
import { BASE_URL } from "@/assets/constants/constants";
import circleArrow from "@/assets/images/inspections-table/circleArrow.svg";
import { Icon } from "@/components/icons/Icon";
import { cn } from "@/lib/styles.utils";
import { getInspectionPDF } from "@/lib/services/inspections";
import { useLoader } from "@/context/loader-context/LoaderContext";
import { appToast } from "@/lib/toast";

export interface InspectionAcitivitiesProps {
  inspectionId: string | number;
  isClosedInspection?: boolean;
}

const InspectionAcitivities = ({
  inspectionId,
  isClosedInspection,
}: InspectionAcitivitiesProps) => {
  const { showLoader, hideLoader } = useLoader();
  const translate = useTranslate();
  const containerRef = useRef<HTMLDivElement>(null);

  const [visibleActions, setVisibleActions] = useState(false);

  const modifyActionsVisibility = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    setVisibleActions((prevVisibility) => !prevVisibility);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setVisibleActions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getPdf = async () => {
    try {
      showLoader();
      const response = await getInspectionPDF({ id: inspectionId as number });

      const url = window.URL.createObjectURL(
        new Blob([response], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `inspection_${inspectionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      appToast.success(translate("success"), translate("pdfDownloadSuccess"));
    } catch (error) {
      const e = error as Error;
      appToast.error(translate("error"), e.message);
    } finally {
      hideLoader();
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {!visibleActions ? (
        <button type="button" onClick={modifyActionsVisibility}>
          <Icon name="ellipsis" className="size-6 text-ib-black" />
        </button>
      ) : (
        <div
          className={cn(
            "absolute right-0 z-10 transform flex flex-col items-end h-6 w-[180px]",
            !isClosedInspection && "-translate-y-3",
          )}
        >
          <button type="button" onClick={modifyActionsVisibility}>
            <Icon name="closeEmptyCircle" className="size-6 text-ib-red-main" />
          </button>
          <div className="w-full bg-ib-white rounded-sm border border-ib-gray-lightest flex flex-col items-end">
            {isClosedInspection ? (
              <div className="flex flex-row justify-end mt-3">
                <button
                  data-testid="pdf-button"
                  value={inspectionId}
                  onClick={getPdf}
                  onTouchStart={getPdf}
                  className="h-10 flex flex-row justify-end gap-x-2 ib-text-main items-center pr-4"
                >
                  {translate("exportPdf")}
                  <Icon
                    name="downloadPdf"
                    className="text-ib-red-main size-6"
                  />
                </button>
              </div>
            ) : null}
            <div className="flex flex-row justify-end mt-3 mb-3">
              <Link to={`${BASE_URL}/${inspectionId}`}>
                <button
                  data-testid="open-inspection-button"
                  className="h-10 flex flex-row justify-end gap-x-2 ib-text-main items-center pr-4"
                >
                  {translate("goToInspection")}
                  <img className="h-[24px] min-w-[20px]" src={circleArrow} />
                </button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(InspectionAcitivities);
